import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@/shared/icons';
import { createPortal } from 'react-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { Tooltip } from '@/shared/components/ui/Tooltip';

/**
 * Member Avatar List Component
 *
 * Displays overlapping avatars of members with a dropdown to add/remove members.
 * Used in Channels and Teams modules for showing and managing assigned members.
 * DS: avatares sem foto = slate (default do AvatarFallback); dropdown portal =
 * claro-fixo (regra "dropdowns sempre claros").
 *
 * @example
 * <MemberAvatarList
 *   members={[{ id: '1', name: 'John Doe', avatar: 'url' }]}
 *   availableUsers={allUsers}
 *   onAddMember={(userId) => handleAdd(userId)}
 *   onRemoveMember={(userId) => handleRemove(userId)}
 *   limit={4}
 * />
 */
export interface MemberAvatarListProps {
  /** Array of current members */
  members?: Array<{ id: string; name: string; avatar?: string }>;
  /** Array of available users to add */
  availableUsers?: Array<{ id: string; name: string; avatar?: string }>;
  /** Maximum number of avatars to show before "+N" badge */
  limit?: number;
  /** Callback when adding a member */
  onAddMember?: (userId: string) => void;
  /** Callback when removing a member */
  onRemoveMember?: (userId: string) => void;
  /** Whether remove functionality is enabled */
  canRemoveMembers?: boolean;
  /** Message to show when no members */
  emptyMessage?: string;
  /** Size of avatars */
  size?: 'sm' | 'md' | 'lg';
  /** Loading state */
  loading?: boolean;
  /** Placeholder for search input */
  placeholder?: string;
}

export const MemberAvatarList: React.FC<MemberAvatarListProps> = ({
  members = [],
  availableUsers = [],
  limit = 4,
  onAddMember,
  onRemoveMember,
  canRemoveMembers = true,
  emptyMessage = 'Nenhum membro',
  size = 'md',
  loading = false,
  placeholder = 'Buscar usuário...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Avatar size classes
  const sizeClasses = {
    sm: 'h-6 w-6 text-xs',
    md: 'h-8 w-8 text-xs',
    lg: 'h-10 w-10 text-sm',
  };

  const avatarSize = sizeClasses[size];

  // Function to get initials from name
  const getInitials = (name?: string) => {
    if (!name) return '??';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Filtrar usuários disponíveis (excluindo já adicionados)
  const memberIds = members.map(m => m.id);
  const filteredUsers = availableUsers
    .filter(user => !memberIds.includes(user.id))
    .filter(user => user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
    }
  }, [isOpen]);

  const handleAddUser = (userId: string) => {
    onAddMember?.(userId);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleRemoveUser = (userId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onRemoveMember?.(userId);
  };

  // Add button (brand outline → brand fill on hover). Ação, não avatar → mantém marca.
  const addButtonClasses = `${avatarSize} rounded-full flex items-center justify-center transition-colors bg-[var(--theme-card-background)] border-2 border-[var(--theme-primary)] text-[var(--theme-primary)] hover:bg-[var(--theme-primary)] hover:text-white`;

  // Dropdown de usuários (portal, claro-fixo)
  const renderDropdown = (emptyLabel: string, withFooter: boolean) => (
    createPortal(
      <>
        <div className="fixed inset-0 z-[100]" onClick={() => setIsOpen(false)} />
        <div
          className="fixed w-72 rounded-md shadow-lg z-[101] max-h-80 flex flex-col"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
          }}
        >
          <div className="p-3 border-b" style={{ borderColor: '#e5e7eb' }}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={placeholder}
              className="w-full px-3 py-1.5 text-sm rounded border"
              style={{ backgroundColor: '#f9fafb', borderColor: '#d1d5db', color: '#1e293b' }}
              autoFocus
              data-testid="member-avatar-search"
            />
          </div>

          <div className="overflow-y-auto flex-1 p-2">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-4 text-sm" style={{ color: '#9ca3af' }}>
                {searchTerm ? 'Nenhum usuário encontrado' : emptyLabel}
              </div>
            ) : (
              <div className="space-y-1">
                {filteredUsers.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => handleAddUser(user.id)}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded text-sm transition-colors text-left"
                    style={{ backgroundColor: 'transparent', color: '#1e293b' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    data-testid={`member-avatar-add-option-${user.id}`}
                  >
                    {/* F83: avatar de pessoa = squircle (convenção do app) */}
                    <Avatar className="w-8 h-8" isSquircle>
                      <AvatarImage src={user.avatar} alt={user.name} isSquircle />
                      <AvatarFallback isSquircle>
                        <span className="text-xs font-medium">{getInitials(user.name)}</span>
                      </AvatarFallback>
                    </Avatar>
                    <span className="flex-1">{user.name}</span>
                    <div
                      className="w-4 h-4 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: '#f3f4f6', color: '#8b5cf6' }}
                    >
                      <Icon name="plus" className="w-3 h-3" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {withFooter && members.length > 0 && (
            <div className="px-3 py-2 border-t text-xs" style={{ borderColor: '#e5e7eb', color: '#64748b' }}>
              {members.length} membro{members.length !== 1 ? 's' : ''} adicionado{members.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </>,
      document.body
    )
  );

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <div className={`${avatarSize} rounded-full animate-pulse`} style={{ backgroundColor: 'var(--theme-surface-sunken)' }} />
      </div>
    );
  }

  // Show only "+" button if no members and onAddMember is provided
  if (members.length === 0) {
    if (onAddMember) {
      return (
        <>
          <Tooltip content="Adicionar membro">
            <button
              ref={buttonRef}
              onClick={() => setIsOpen(!isOpen)}
              className={addButtonClasses}
              data-testid="member-avatar-add"
            >
              <Icon name="plus" className="w-4 h-4" />
            </button>
          </Tooltip>
          {isOpen && renderDropdown('Nenhum usuário disponível', false)}
        </>
      );
    }

    return <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>{emptyMessage}</span>;
  }

  // Calculate displayed members and remaining count
  const displayedMembers = members.slice(0, limit);
  const remainingCount = members.length > limit ? members.length - limit : 0;

  return (
    <>
      <div className="flex items-center -space-x-2">
        {/* Member Avatars */}
        {displayedMembers.map((member, index) => (
          <div key={member.id} className="relative group">
            {/* F83: avatar de pessoa = squircle (convenção do app) */}
            <Tooltip content={member.name}>
              <Avatar
                className={`${avatarSize} border-2 border-white hover:scale-110 transition-transform cursor-pointer`}
                style={{ zIndex: 10 - index }}
                isSquircle
              >
                <AvatarImage src={member.avatar} alt={member.name} isSquircle />
                <AvatarFallback isSquircle>{getInitials(member.name)}</AvatarFallback>
              </Avatar>
            </Tooltip>
            {/* Remove button on hover (only if canRemoveMembers is true) */}
            {onRemoveMember && canRemoveMembers && (
              <button
                type="button"
                onClick={(e) => handleRemoveUser(member.id, e)}
                className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hidden group-hover:flex"
                data-testid={`member-avatar-remove-${member.id}`}
              >
                <Icon name="x" className="w-2.5 h-2.5 text-white" />
              </button>
            )}
          </div>
        ))}

        {/* Remaining Count Badge */}
        {remainingCount > 0 && (
          <Tooltip content={`+${remainingCount} membros`}>
            <div
              className={`${avatarSize} rounded-full border-2 border-white flex items-center justify-center font-medium`}
              style={{ zIndex: 7, backgroundColor: 'var(--theme-button-secondary)', color: 'var(--theme-button-secondary-text)' }}
            >
              +{remainingCount}
            </div>
          </Tooltip>
        )}

        {/* Add Button */}
        {onAddMember && (
          <Tooltip content="Adicionar membro">
            <button
              ref={buttonRef}
              onClick={() => setIsOpen(!isOpen)}
              className={`${addButtonClasses} z-10 ml-2`}
              data-testid="member-avatar-add"
            >
              <Icon name="plus" className="w-4 h-4" />
            </button>
          </Tooltip>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && renderDropdown('Todos os usuários já foram adicionados', true)}
    </>
  );
};

MemberAvatarList.displayName = 'MemberAvatarList';
